import React from 'react';
import {
  Alert,
  Box,
  FormControl,
  MenuItem,
  Select,
  Typography,
  FormHelperText,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { REFUND_POLICY_OPTIONS } from '../partnerEventSettings';
import { mergeFieldSx } from '../components/manageEventFieldStyles';
import EventPublishedSwitch from '../../widgets/EventPublishedSwitch';
import RequireJoinApprovalField from '../../../shared/registration/RequireJoinApprovalField';
import AttendeeEligibilityField from '../../../shared/registration/AttendeeEligibilityField';

const SettingRow = ({ title, description, children, dimmed = false }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'space-between',
      gap: 3,
      py: 2.5,
      borderBottom: '1px solid',
      borderColor: 'divider',
    }}
  >
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography
        sx={{
          fontWeight: 600,
          fontSize: '0.9375rem',
          color: dimmed ? 'text.disabled' : 'text.primary',
          mb: 0.5,
        }}
      >
        {title}
      </Typography>
      <Typography
        sx={{
          fontSize: '0.8125rem',
          color: dimmed ? 'text.disabled' : 'text.secondary',
          lineHeight: 1.45,
        }}
      >
        {description}
      </Typography>
    </Box>
    <Box sx={{ flexShrink: 0, pt: 0.25 }}>{children}</Box>
  </Box>
);

const SettingsPoliciesStep = ({ form, errors = {}, onChange }) => (
  <Box>
    <Alert
      severity="info"
      sx={{
        mb: 2.5,
        borderRadius: 2,
        bgcolor: 'action.hover',
        color: 'text.secondary',
        '& .MuiAlert-icon': { color: 'text.secondary' },
      }}
    >
      Additional settings and policies are coming soon.
    </Alert>

    <AttendeeEligibilityField
      value={form.attendeeEligibility}
      onChange={(value) => onChange('attendeeEligibility', value)}
      error={Boolean(errors.attendeeEligibility)}
      helperText={errors.attendeeEligibility}
    />

    <Box
      sx={{
        pointerEvents: 'none',
        userSelect: 'none',
      }}
      aria-disabled="true"
    >
      <SettingRow
        dimmed
        title="Enable Waitlist"
        description="Automatically collect emails when capacity is reached."
      >
        <EventPublishedSwitch
          checked={Boolean(form.enableWaitlist)}
          disabled
          onChange={(e) => onChange('enableWaitlist', e.target.checked)}
          inputProps={{ 'aria-label': 'Enable waitlist' }}
        />
      </SettingRow>
    </Box>

    <Box
      sx={{
        pointerEvents: 'none',
        userSelect: 'none',
      }}
      aria-disabled="true"
    >
      <SettingRow
        dimmed
        title="Invite-Only"
        description="Hide from public directory, accessible only via direct link."
      >
        <EventPublishedSwitch
          checked={Boolean(form.inviteOnly)}
          disabled
          onChange={(e) => onChange('inviteOnly', e.target.checked)}
          inputProps={{ 'aria-label': 'Invite only' }}
        />
      </SettingRow>
    </Box>

    <Box
      sx={{
        pointerEvents: 'none',
        userSelect: 'none',
      }}
      aria-disabled="true"
    >
      <RequireJoinApprovalField
        checked={form.requireJoinApproval}
        onChange={() => {}}
        disabled
        dimmed
      />
    </Box>

    <Box
      sx={{
        pointerEvents: 'none',
        userSelect: 'none',
        pt: 0.5,
      }}
      aria-disabled="true"
    >
      <Typography sx={{ fontWeight: 600, fontSize: '0.9375rem', color: 'text.disabled', mb: 1.5 }}>
        Refund Policy
      </Typography>
      <FormControl
        fullWidth
        disabled
        error={Boolean(errors.refundPolicy)}
        sx={mergeFieldSx(Boolean(errors.refundPolicy))}
      >
        <Select
          value={form.refundPolicy ?? ''}
          disabled
          onChange={(e) => onChange('refundPolicy', e.target.value)}
          displayEmpty
          IconComponent={KeyboardArrowDownIcon}
          sx={{
            borderRadius: 2,
            bgcolor: 'action.hover',
            fontSize: '0.875rem',
          }}
        >
          <MenuItem value="" disabled>
            Select refund policy
          </MenuItem>
          {REFUND_POLICY_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
        {errors.refundPolicy ? <FormHelperText>{errors.refundPolicy}</FormHelperText> : null}
      </FormControl>
    </Box>
  </Box>
);

export default SettingsPoliciesStep;
